import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../lib/config';
import { useShigoSession } from '../../lib/useShigoSession';
import { createDebugClient } from './api';
import type { DebugListFilter, DebugRuleRequest } from './model';

const debugQueryKey = ['debug'] as const;
export const debugRulesListsQueryKey = [...debugQueryKey, 'list'] as const;

function useDebugClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createDebugClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useDebugRulesQuery(filter: DebugListFilter) {
  const client = useDebugClient();
  return useQuery({
    queryKey: [...debugRulesListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useDebugPublisherOptions(searchTerm: string, selectedId?: string) {
  const client = useDebugClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [...debugQueryKey, 'publishers', normalizedSearch, hydrateId ?? ''],
    queryFn: () => client.listPublishers({ searchTerm: normalizedSearch, selectedId: hydrateId }),
    enabled: Boolean(normalizedSearch || hydrateId),
  });
}

export function useDebugPlacementOptions(
  searchTerm: string,
  publisherId?: string,
  selectedId?: string,
) {
  const client = useDebugClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [
      ...debugQueryKey,
      'placements',
      normalizedSearch,
      publisherId ?? '',
      hydrateId ?? '',
    ],
    queryFn: () =>
      client.listPlacements({
        searchTerm: normalizedSearch,
        publisherId,
        selectedId: hydrateId,
      }),
    enabled: Boolean(normalizedSearch || hydrateId),
  });
}

export function useDebugDemandPartnerOptions(searchTerm: string, selectedId?: string) {
  const client = useDebugClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [...debugQueryKey, 'demand-partners', normalizedSearch, hydrateId ?? ''],
    queryFn: () =>
      client.listDemandPartners({ searchTerm: normalizedSearch, selectedId: hydrateId }),
    enabled: Boolean(normalizedSearch || hydrateId),
  });
}

export function useDebugRuleMutations() {
  const client = useDebugClient();
  const queryClient = useQueryClient();
  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: debugRulesListsQueryKey });
  return {
    create: useMutation({
      mutationFn: (payload: DebugRuleRequest) => client.create(payload),
      onSuccess: invalidateLists,
    }),
    update: useMutation({
      mutationFn: ({ jobId, payload }: { jobId: string; payload: DebugRuleRequest }) =>
        client.update(jobId, payload),
      onSuccess: invalidateLists,
    }),
  };
}
